import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useToast } from '../context/ToastContext.jsx'
import Seo from '../components/Seo.jsx'

const SITE = 'https://calculatemypeptides.com'

// Common sources with their correct medium, so channel grouping in GA4 is right
// and spelling stays consistent (tiktok, not "Tik Tok").
const SOURCES = [
  { source: 'tiktok', medium: 'social', label: 'TikTok' },
  { source: 'instagram', medium: 'social', label: 'Instagram' },
  { source: 'youtube', medium: 'social', label: 'YouTube' },
  { source: 'reddit', medium: 'social', label: 'Reddit' },
  { source: 'x', medium: 'social', label: 'X / Twitter' },
  { source: 'facebook', medium: 'social', label: 'Facebook' },
  { source: 'newsletter', medium: 'email', label: 'Newsletter' },
]

const clean = (s) => s.trim().toLowerCase().replace(/\s+/g, '-')

function buildUrl({ path, source, medium, campaign, content, term }) {
  let base
  try {
    base = new URL(path.startsWith('http') ? path : SITE + (path.startsWith('/') ? path : '/' + path))
  } catch {
    return ''
  }
  const set = (k, v) => v && v.trim() && base.searchParams.set(k, clean(v))
  set('utm_source', source)
  set('utm_medium', medium)
  set('utm_campaign', campaign)
  set('utm_content', content)
  set('utm_term', term)
  return base.toString()
}

export default function AdminUtm() {
  const toast = useToast()
  const [f, setF] = useState({ path: '/', source: 'tiktok', medium: 'social', campaign: 'bio', content: '', term: '' })
  const [copied, setCopied] = useState('')
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))

  const url = useMemo(() => buildUrl(f), [f])

  const copy = async (text, id = 'main') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(id)
      toast('Link copied', 'green')
      setTimeout(() => setCopied(''), 1800)
    } catch {
      toast('Couldn’t copy — select and copy manually', 'red')
    }
  }

  const pickSource = (s) => setF((prev) => ({ ...prev, source: s.source, medium: s.medium }))

  // Ready-made bio links (one per platform), pointing at the homepage.
  const bioLinks = SOURCES.map((s) => ({
    label: s.label,
    url: buildUrl({ path: '/', source: s.source, medium: s.medium, campaign: 'bio' }),
  }))

  return (
    <main className="page">
      <Seo title="UTM Link Builder | CalculateMyPeptides" noindex />
      <div className="container page-narrow">
        <Link to="/admin" className="pep-back">← Admin</Link>
        <span className="pill blue" style={{ marginBottom: 8 }}>Admin</span>
        <h1 className="section-title">UTM Link Builder</h1>
        <p className="section-sub" style={{ maxWidth: '62ch' }}>
          Build correctly-tagged links so GA4 attributes traffic to the right source. Social apps strip the
          referrer, so a link you post to TikTok shows as “Direct” unless it carries these tags.
        </p>

        <div className="card" style={{ padding: 18, marginTop: 8 }}>
          <div className="field">
            <span className="field-label">Common source (fills source + medium)</span>
            <div className="chart-chips" style={{ marginTop: 4 }}>
              {SOURCES.map((s) => (
                <button
                  key={s.source}
                  type="button"
                  className={`chart-chip ${f.source === s.source ? 'active' : ''}`}
                  onClick={() => pickSource(s)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="utm-grid">
            <label className="field"><span className="field-label">Destination path</span>
              <input className="input" value={f.path} onChange={set('path')} placeholder="/ or /calculator" /></label>
            <label className="field"><span className="field-label">utm_source</span>
              <input className="input" value={f.source} onChange={set('source')} placeholder="tiktok" /></label>
            <label className="field"><span className="field-label">utm_medium</span>
              <input className="input" value={f.medium} onChange={set('medium')} placeholder="social" /></label>
            <label className="field"><span className="field-label">utm_campaign</span>
              <input className="input" value={f.campaign} onChange={set('campaign')} placeholder="bio" /></label>
            <label className="field"><span className="field-label">utm_content <span className="muted">(optional)</span></span>
              <input className="input" value={f.content} onChange={set('content')} placeholder="bio-link" /></label>
            <label className="field"><span className="field-label">utm_term <span className="muted">(optional)</span></span>
              <input className="input" value={f.term} onChange={set('term')} placeholder="" /></label>
          </div>

          <span className="field-label">Your tagged link</span>
          <div className="embed-code">
            <button className="btn btn-primary btn-sm embed-copy" onClick={() => copy(url)}>{copied === 'main' ? '✓ Copied' : 'Copy'}</button>
            <textarea className="embed-textarea" readOnly rows={2} value={url} onFocus={(e) => e.target.select()} />
          </div>
          <p className="muted small" style={{ marginTop: 8 }}>
            Never put UTM tags on links between your own pages — it resets the session and breaks attribution.
          </p>
        </div>

        <h2 className="pep-h2" style={{ marginTop: 26 }}>Ready-made bio links</h2>
        <p className="soft" style={{ marginTop: 0 }}>One tagged homepage link per platform — drop these straight into your profiles.</p>
        <div className="admin-list">
          {bioLinks.map((b) => (
            <div className="card admin-row" key={b.label} style={{ padding: 12 }}>
              <div style={{ minWidth: 0 }}>
                <strong>{b.label}</strong>
                <p className="muted small" style={{ margin: '3px 0 0', wordBreak: 'break-all' }}>{b.url}</p>
              </div>
              <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }} onClick={() => copy(b.url, b.label)}>
                {copied === b.label ? '✓' : 'Copy'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </main>
  )
}
